import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useModal } from '../contexts/ModalContext';
import { useLanguage } from '../contexts/LanguageContext';
import { STATUS_CHANGE_REASONS } from '../constants/status';
import type { RealtimeChannel } from '@supabase/supabase-js';

function getStatusChangeMessage(fromStatus: string, toStatus: string, t: (key: string) => string): string {
  const reason = STATUS_CHANGE_REASONS.find(
    r => r.fromStatus === fromStatus && r.toStatus === toStatus
  );

  if (reason) {
    return t(reason.messageKey);
  }

  return t('statusToast.statusChanged').replace('{status}', t(`status.${toStatus.toLowerCase().replace(/\s+/g, '')}`));
}

export function useStatusChangeNotification(campaignId: string | null, currentStatus: string | null) {
  const { toast } = useModal();
  const { t } = useLanguage();
  const previousStatus = useRef<string | null>(currentStatus);
  const channelRef = useRef<RealtimeChannel | null>(null);

  const showStatusToast = useCallback((fromStatus: string, toStatus: string) => {
    const message = getStatusChangeMessage(fromStatus, toStatus, t);
    toast.info(message, 5000);
  }, [toast, t]);

  useEffect(() => {
    previousStatus.current = currentStatus;
  }, []);

  useEffect(() => {
    if (!campaignId) return;

    channelRef.current = supabase
      .channel(`campaign-status-toast:${campaignId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'campaigns',
          filter: `id=eq.${campaignId}`,
        },
        (payload) => {
          if (payload.new && payload.old && 'campaign_status' in payload.new && 'campaign_status' in payload.old) {
            const newStatus = payload.new.campaign_status as string;
            const oldStatus = payload.old.campaign_status as string;

            if (newStatus !== oldStatus && previousStatus.current !== newStatus) {
              showStatusToast(oldStatus, newStatus);
              previousStatus.current = newStatus;
            }
          }
        }
      )
      .subscribe();

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [campaignId, showStatusToast]);

  useEffect(() => {
    if (currentStatus && previousStatus.current && currentStatus !== previousStatus.current) {
      previousStatus.current = currentStatus;
    }
  }, [currentStatus]);
}
